import { useCallback, useEffect, useState } from 'react';
import {
  fbaopsApi,
  type BoxRecord,
  type FBAOpsDashboard,
  type FBARecommendation,
  type InboundShipmentRecord,
  type InventoryHealthRecord,
  type InventoryRecord,
  type ReceivingRecord,
  type SyncJobRecord,
  type WarehouseCapacityRecord,
} from '../../api/fbaops';

type Tab = 'dashboard' | 'inventory' | 'shipments' | 'boxes' | 'capacity' | 'receiving' | 'health' | 'recommendations';

function statusClass(s: string) {
  if (s === 'completed' || s === 'available') return 'text-emerald-700 bg-emerald-50';
  if (s === 'in_transit' || s === 'receiving' || s === 'working') return 'text-sky-700 bg-sky-50';
  if (s === 'delayed' || s === 'discrepancy') return 'text-red-700 bg-red-50';
  if (s === 'draft' || s === 'inbound') return 'text-amber-700 bg-amber-50';
  return 'text-slate-600 bg-slate-50';
}

function healthClass(score: number) {
  if (score >= 80) return 'text-emerald-700';
  if (score >= 60) return 'text-amber-700';
  return 'text-red-700';
}

function riskClass(r?: string) {
  if (r === 'high') return 'text-red-700 bg-red-50';
  if (r === 'medium') return 'text-amber-700 bg-amber-50';
  return 'text-emerald-700 bg-emerald-50';
}

function severityClass(s?: string) {
  if (s === 'critical') return 'text-red-700 bg-red-50';
  if (s === 'high') return 'text-orange-700 bg-orange-50';
  if (s === 'medium') return 'text-amber-700 bg-amber-50';
  return 'text-slate-600 bg-slate-50';
}

export function FBAOpsPage() {
  const [tab, setTab] = useState<Tab>('dashboard');
  const [dashboard, setDashboard] = useState<FBAOpsDashboard | null>(null);
  const [inventory, setInventory] = useState<InventoryRecord[]>([]);
  const [shipments, setShipments] = useState<InboundShipmentRecord[]>([]);
  const [boxes, setBoxes] = useState<BoxRecord[]>([]);
  const [capacity, setCapacity] = useState<WarehouseCapacityRecord[]>([]);
  const [receiving, setReceiving] = useState<ReceivingRecord[]>([]);
  const [health, setHealth] = useState<InventoryHealthRecord[]>([]);
  const [recommendations, setRecommendations] = useState<FBARecommendation[]>([]);
  const [syncJobs, setSyncJobs] = useState<SyncJobRecord[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, inv, shps, bx, cap, rcv, hlth, recs, sync] = await Promise.all([
        fbaopsApi.dashboard(),
        fbaopsApi.listInventory(),
        fbaopsApi.listShipments(),
        fbaopsApi.listBoxes(),
        fbaopsApi.listCapacity(),
        fbaopsApi.listReceiving(),
        fbaopsApi.listHealth(),
        fbaopsApi.listRecommendations(),
        fbaopsApi.listSyncJobs(),
      ]);
      setDashboard(dash);
      setInventory(inv);
      setShipments(shps);
      setBoxes(bx);
      setCapacity(cap);
      setReceiving(rcv);
      setHealth(hlth);
      setRecommendations(recs);
      setSyncJobs(sync);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load FBA operations data');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const generateRecs = async () => {
    setBusy(true);
    try {
      await fbaopsApi.generateRecommendations();
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'dashboard', label: 'Inventory Dashboard' },
    { id: 'inventory', label: 'Inventory Explorer' },
    { id: 'shipments', label: 'Inbound Shipments' },
    { id: 'boxes', label: 'Box Manager' },
    { id: 'capacity', label: 'Capacity Dashboard' },
    { id: 'receiving', label: 'Receiving Center' },
    { id: 'health', label: 'Inventory Health' },
    { id: 'recommendations', label: 'AI Recommendations' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">FBA Inventory &amp; Warehouse Intelligence</h1>
          <p className="text-sm text-slate-500">
            Inventory sync, inbound shipments, warehouse capacity, receiving &amp; AI copilot — APP-00003
          </p>
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={busy}
          className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-50"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
              tab === t.id ? 'bg-sky-100 text-sky-700' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'dashboard' && dashboard && (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Total SKUs', value: dashboard.total_skus.toString() },
              { label: 'Available Units', value: dashboard.available_units.toLocaleString() },
              { label: 'Inbound Units', value: dashboard.inbound_units.toLocaleString() },
              { label: 'Active Shipments', value: dashboard.active_shipments.toString() },
              { label: 'Delayed Shipments', value: dashboard.delayed_shipments.toString() },
              { label: 'Avg Health Score', value: dashboard.avg_health_score.toFixed(0) },
              { label: 'Capacity Utilization', value: `${dashboard.capacity_utilization_pct.toFixed(1)}%` },
              { label: 'Active Reconciliations', value: dashboard.active_reconciliations.toString() },
            ].map((kpi) => (
              <div key={kpi.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{kpi.label}</p>
                <p className="mt-1 text-2xl font-semibold text-slate-900">{kpi.value}</p>
              </div>
            ))}
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="font-medium text-slate-900">Sync Jobs</h3>
              <div className="mt-3 space-y-2">
                {syncJobs.map((j) => (
                  <div key={j.id} className="flex items-center justify-between text-sm">
                    <span>{j.mode} sync</span>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(j.status === 'completed' ? 'completed' : j.status === 'running' ? 'working' : 'draft')}`}>
                      {j.status} · {j.items_synced} items
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="font-medium text-slate-900">Warehouse Capacity</h3>
              <div className="mt-3 space-y-2">
                {capacity.map((c) => (
                  <div key={c.id} className="flex items-center justify-between text-sm">
                    <span>{c.fulfillment_center}</span>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${riskClass(c.risk_level)}`}>
                      {c.utilization_pct.toFixed(1)}% · {c.risk_level}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'inventory' && (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2">SKU</th>
                <th className="px-4 py-2">Type</th>
                <th className="px-4 py-2">FC</th>
                <th className="px-4 py-2">Qty</th>
                <th className="px-4 py-2">DoC</th>
                <th className="px-4 py-2">Health</th>
                <th className="px-4 py-2">Flags</th>
              </tr>
            </thead>
            <tbody>
              {inventory.map((inv) => (
                <tr key={inv.id} className="border-t border-slate-100">
                  <td className="px-4 py-2">
                    <p className="font-mono text-xs">{inv.sku}</p>
                    <p className="text-xs text-slate-500 truncate max-w-xs">{inv.title}</p>
                  </td>
                  <td className="px-4 py-2">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(inv.inventory_type)}`}>
                      {inv.inventory_type.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-2">{inv.fulfillment_center}</td>
                  <td className="px-4 py-2">{inv.quantity.toLocaleString()}</td>
                  <td className="px-4 py-2">{inv.days_of_cover > 0 ? inv.days_of_cover.toFixed(0) : '—'}</td>
                  <td className={`px-4 py-2 font-medium ${healthClass(inv.health_score)}`}>{inv.health_score.toFixed(0)}</td>
                  <td className="px-4 py-2 text-xs">
                    {inv.is_critical && <span className="mr-1 rounded bg-red-50 px-1 text-red-700">Critical</span>}
                    {inv.is_slow_moving && <span className="rounded bg-amber-50 px-1 text-amber-700">Slow</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'shipments' && (
        <div className="space-y-3">
          {shipments.map((s) => (
            <div key={s.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="font-mono text-sm font-medium">{s.shipment_id}</p>
                  <p className="text-sm text-slate-600">{s.shipment_name}</p>
                </div>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(s.status)}`}>
                  {s.status.replace(/_/g, ' ')}
                </span>
              </div>
              <div className="mt-2 flex flex-wrap gap-4 text-xs text-slate-500">
                <span>→ {s.destination_fc}</span>
                <span>{s.box_count} boxes · {s.unit_count} units</span>
                <span>Received: {s.received_units}/{s.shipped_units}</span>
                {s.carrier && <span>{s.carrier}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'boxes' && (
        <div className="space-y-3">
          {boxes.map((b) => (
            <div key={b.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="font-mono text-sm">{b.box_id}</p>
                  <p className="text-xs text-slate-500">Shipment: {b.shipment_id}</p>
                </div>
                {!b.is_valid && (
                  <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs text-red-700">invalid</span>
                )}
              </div>
              <div className="mt-2 flex gap-4 text-sm text-slate-600">
                <span>{b.weight_lbs} lbs</span>
                <span>{b.length_in}×{b.width_in}×{b.height_in} in</span>
                <span>{b.unit_count} units</span>
                {b.label_printed && <span className="text-emerald-600">Label printed</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'capacity' && (
        <div className="space-y-3">
          {capacity.map((c) => (
            <div key={c.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="font-medium text-slate-900">{c.fulfillment_center}</h3>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${riskClass(c.risk_level)}`}>
                  {c.risk_level} risk
                </span>
              </div>
              <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
                <div
                  className={`h-full rounded-full ${c.utilization_pct > 90 ? 'bg-red-500' : c.utilization_pct > 75 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                  style={{ width: `${Math.min(c.utilization_pct, 100)}%` }}
                />
              </div>
              <div className="mt-2 flex gap-4 text-sm text-slate-600">
                <span>{c.current_utilization.toLocaleString()} / {c.capacity_limit.toLocaleString()}</span>
                <span>Restock limit: {c.restock_limit.toLocaleString()}</span>
                <span>Inbound limit: {c.inbound_limit.toLocaleString()}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'receiving' && (
        <div className="space-y-3">
          {receiving.map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-sm">{r.shipment_id}</span>
                <span className="font-mono text-xs">{r.sku}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(r.status)}`}>
                  {r.status}
                </span>
              </div>
              <div className="mt-2 text-sm text-slate-600">
                Shipped: {r.shipped_qty} · Received: {r.received_qty}
                {r.discrepancy !== 0 && <span className="ml-2 text-red-600">Δ {r.discrepancy}</span>}
              </div>
              {r.issue && <p className="mt-1 text-xs text-slate-500">{r.issue}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'health' && (
        <div className="space-y-3">
          {health.map((h) => (
            <div key={h.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-sm">{h.sku}</span>
                <span className={`font-medium ${healthClass(h.health_score)}`}>{h.health_score.toFixed(0)}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${severityClass(h.severity)}`}>
                  {h.severity}
                </span>
              </div>
              <div className="mt-2 flex gap-4 text-xs text-slate-500">
                <span>DoC: {h.days_of_cover.toFixed(0)}d</span>
                <span>Turnover: {h.turnover_rate.toFixed(1)}</span>
                <span>LTSF risk: {(h.storage_fee_risk * 100).toFixed(0)}%</span>
              </div>
              {h.issue && <p className="mt-1 text-sm text-slate-600">{h.issue}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'recommendations' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={generateRecs}
              disabled={busy}
              className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50"
            >
              Generate AI Recommendations
            </button>
          </div>
          {recommendations.map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <h3 className="font-medium text-slate-900">{r.title}</h3>
                  {r.description && <p className="mt-1 text-sm text-slate-600">{r.description}</p>}
                </div>
                <span className="rounded-full bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-700">
                  #{r.priority_rank}
                </span>
              </div>
              <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-500">
                {r.sku && <span>SKU: {r.sku}</span>}
                {r.shipment_id && <span>Shipment: {r.shipment_id}</span>}
                <span>{r.type.replace(/_/g, ' ')}</span>
                <span>Confidence: {(r.confidence_score * 100).toFixed(0)}%</span>
                {r.expected_impact && <span>Impact: {r.expected_impact}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
